import React, { useState } from 'react';
import { useList } from 'src/hooks/useFetch.hook';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  useTheme
} from '@mui/material';
import { Icon } from '@iconify/react';
import trendingUpFill from '@iconify/icons-eva/trending-up-fill';
import clockFill from '@iconify/icons-eva/clock-fill';
import activityFill from '@iconify/icons-eva/activity-fill';
import peopleFill from '@iconify/icons-eva/people-fill';
import { statusIconMap, statusColorMap  } from '../../constants/ocpi.constant';
import { capitalizeFirstLetter } from 'src/utils/helper';

const getMetricCards = (data) => [
  {
    title: 'Success Rate',
    value: `${data?.metrics?.successRate?.toFixed(1) || 0}%`,
    color: 'success',
    icon: trendingUpFill,
    trend: 'Last 24 hours'
  },
  {
    title: 'Avg Response Time',
    value: `${data?.metrics?.avgResponseTime?.toFixed(1) || 0}s`,
    color: 'info',
    icon: clockFill,
    trend: 'Last 24 hours'
  },
  {
    title: 'Total Commands',
    value: `${data?.commandStats?.total || 0}`,
    color: 'warning',
    icon: activityFill,
    trend: 'All time'
  },
  {
    title: 'Active Partners',
    value: `${data?.metrics?.activePartners || 0}`,
    color: 'error',
    icon: peopleFill,
    trend: 'With recent activity'
  }
];

export default function OverviewTab({ data, onSendCommand }) {
  const { data: activeCommandsResponse, isLoading: activeCommandsLoading } = useList("activeCommands");
  const activeCommands = activeCommandsResponse || [];
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();
  const metricCards = getMetricCards(data);

  const handleQuickAction = (actionType) => {
    const command = {
      type: actionType,
      status: 'PENDING',
      created: new Date().toISOString(),
      partner: 'Quick Action'
    };
    
    if (data?.sendQuickCommand) {
      data.sendQuickCommand(command);
    } else if (onSendCommand) {
      onSendCommand(command);
    }
  };
  const handleViewDetails = (command) => {
    setSelectedCommand(command);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCommand(null);
  };

  const handleCancelCommand = (commandId) => {
    if (data?.cancelCommand) {
      data.cancelCommand(commandId);
    }
    handleCloseDialog();
  };

  const handleRetryCommand = (commandId) => {
    if (data?.retryCommand) {
      data.retryCommand(commandId);
    }
    handleCloseDialog();
  };
  return (
    <Box sx={{ p: 3 }}>

      {/* Quick Actions */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Send common commands instantly
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleQuickAction('START_SESSION')}
                    sx={{
                      bgcolor: '#00d25b',
                      color: '#fff',
                      minHeight: 100,
                      borderRadius: 2,
                      boxShadow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      px: 3,
                      '&:hover': { bgcolor: '#00b84a' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box component="span" sx={{ fontSize: 28, mr: 1, display: 'flex', alignItems: 'center' }}>▶</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                          Start Session
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9 }}>
                          Start a charging session
                        </Typography>
                      </Box>
                    </Box>
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleQuickAction('STOP_SESSION')}
                    sx={{
                      bgcolor: '#ff5252',
                      color: '#fff',
                      minHeight: 100,
                      borderRadius: 2,
                      boxShadow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      px: 3,
                      '&:hover': { bgcolor: '#e53935' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box component="span" sx={{ fontSize: 28, mr: 1, display: 'flex', alignItems: 'center' }}>■</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                          Stop Session
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9 }}>
                          Stop an active session
                        </Typography>
                      </Box>
                    </Box>
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleQuickAction('UNLOCK_CONNECTOR')}
                    sx={{
                      bgcolor: '#fff',
                      color: '#222',
                      minHeight: 100,
                      borderRadius: 2,
                      boxShadow: 1,
                      borderColor: '#e0e0e0',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      px: 3,
                      '&:hover': { bgcolor: '#f5f5f5', borderColor: '#bdbdbd' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box component="span" sx={{ fontSize: 28, mr: 1, display: 'flex', alignItems: 'center' }}>🔓</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#222', lineHeight: 1 }}>
                          Unlock Connector
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#222', opacity: 0.7 }}>
                          Unlock a connector
                        </Typography>
                      </Box>
                    </Box>
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleQuickAction('RESERVE_NOW')}
                    sx={{
                      bgcolor: '#f6f8fa',
                      color: '#222',
                      minHeight: 100,
                      borderRadius: 2,
                      boxShadow: 1,
                      borderColor: '#e0e0e0',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      px: 3,
                      '&:hover': { bgcolor: '#e3e7ed', borderColor: '#bdbdbd' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box component="span" sx={{ fontSize: 28, mr: 1, display: 'flex', alignItems: 'center' }}>⏲</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#222', lineHeight: 1 }}>
                          Reserve Now
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#222', opacity: 0.7 }}>
                          Create a reservation
                        </Typography>
                      </Box>
                    </Box>
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Commands */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Commands ({activeCommands.length})
              </Typography>
              {activeCommandsLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={24} sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading active commands...
                  </Typography>
                </Box>
              ) : activeCommands.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Icon icon={activityFill} width={48} height={48} style={{ color: '#999', marginBottom: 16 }} />
                  <Typography variant="body2" color="text.secondary">
                    No active commands
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {activeCommands.map((command, index) => (
                    <Box key={command.id || index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                        <Avatar sx={{ bgcolor: 'grey.200', mr: 2 }} variant='rounded'>
                          <Icon className="spin" icon={statusIconMap[capitalizeFirstLetter(command.status.toLowerCase())].icon} width={20} height={20} color={theme.palette[statusIconMap[capitalizeFirstLetter(command.status.toLowerCase())].color].main} />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2">
                            {command.command_type} - {command.command_uid}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Created: {new Date(command.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                        <Chip
                          label={command.status}
                          color={statusColorMap[capitalizeFirstLetter(command.status.toLowerCase())].color}
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1, 
                            bgcolor: statusColorMap[capitalizeFirstLetter(command.status.toLowerCase())].bgcolor,
                            color: statusColorMap[capitalizeFirstLetter(command.status.toLowerCase())].text
                          }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewDetails(command)}
                        >
                          View
                        </Button>
                      </Box>
                      {index < activeCommands.length - 1 && <Divider />}
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Command Details Dialog */}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Command Details
        </DialogTitle>
        <DialogContent>
          {selectedCommand && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Command Type
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedCommand.command_type}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedCommand.status}
                    color={statusColorMap[(capitalizeFirstLetter(selectedCommand.status.toLowerCase()))].color}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      bgcolor: statusColorMap[(capitalizeFirstLetter(selectedCommand.status.toLowerCase()))].bgcolor,
                      color: statusColorMap[(capitalizeFirstLetter(selectedCommand.status.toLowerCase()))].text
                    }}  
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Command UID
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedCommand.command_uid || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(selectedCommand.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                {selectedCommand.token && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Token UID
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedCommand.token.uid}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Token Type
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedCommand.token.type}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Token Contract ID
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedCommand.token.contract_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Token Visual Number
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedCommand.token.visual_number}
                      </Typography>
                    </Grid>
                  </>
                )}
                {selectedCommand.location && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedCommand.location}
                    </Typography>
                  </Grid>
                )}
                {selectedCommand.evse && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      EVSE
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedCommand.evse}
                    </Typography>
                  </Grid>
                )}
                {selectedCommand.connector && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Connector
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedCommand.connector}
                    </Typography>
                  </Grid>
                )}
                {selectedCommand.session && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Session ID
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedCommand.session}
                    </Typography>
                  </Grid>
                )}
                {selectedCommand.command_data && Object.keys(selectedCommand.command_data).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Command Data
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                      <pre style={{ backgroundColor: 'hsl(210, 40%, 96.1%)', margin: 0, padding: '8px', borderRadius: '4px', color: '#64748b' }}>
                        <code>
                          {JSON.stringify(selectedCommand.command_data, null, 2)}
                        </code>
                      </pre>
                    </Typography>
                  </Grid>
                )}
                {selectedCommand.response_url && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Response URL
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', bgcolor: 'grey.50', p: 1, borderRadius: 1, wordBreak: 'break-all' }}>
                      {selectedCommand.response_url}
                    </Typography>
                  </Grid>
                )}
                {selectedCommand.error && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="error">
                      Error Details
                    </Typography>
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      {selectedCommand.error}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedCommand?.status === 'FAILED' && (
            <Button
              color="primary"
              onClick={() => handleRetryCommand(selectedCommand.id)}
            >
              Retry
            </Button>
          )}
          {selectedCommand?.status !== 'COMPLETED' && selectedCommand?.status !== 'FAILED' && (
            <Button
              color="error"
              onClick={() => handleCancelCommand(selectedCommand.id)}
            >
              Cancel
            </Button>
          )}
          <Button onClick={handleCloseDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
